import { defineMessages } from 'react-intl';

/** The copy of the rollout run sheet (design rollout-plan a1–e1, owner decisions 2026-10-09). */
export const rolloutMessages = defineMessages({
  // ---- Summary (a1, b1, e1)
  eyebrow: { id: 'app.sml.releases.rollout.eyebrow', defaultMessage: 'Rollout' },
  countPlanned: {
    id: 'app.sml.releases.rollout.count.planned',
    defaultMessage: 'of {total, plural, one {# post} other {# posts}} planned',
  },
  countOut: {
    id: 'app.sml.releases.rollout.count.out',
    defaultMessage: 'of {total, plural, one {# post} other {# posts}} out',
  },
  emptyLead: {
    id: 'app.sml.releases.rollout.empty.lead',
    defaultMessage: '{steps, plural, one {# step} other {# steps}} from {first} to {last}.',
  },
  emptyBody: {
    id: 'app.sml.releases.rollout.empty.body',
    defaultMessage:
      '{ready, plural, =0 {No post is ready to plan yet.} one {# post is ready to plan: its asset is in the kit.} other {# posts are ready to plan: their assets are in the kit.}} {missing, plural, =0 {} one {# post waits for an asset that you did not make yet.} other {# posts wait for an asset that you did not make yet.}}',
  },
  onTrack: { id: 'app.sml.releases.rollout.onTrack', defaultMessage: 'On track for release day.' },
  needsCount: {
    id: 'app.sml.releases.rollout.needsCount',
    defaultMessage: '{count, plural, one {# thing needs you.} other {# things need you.}}',
  },
  waitingCount: {
    id: 'app.sml.releases.rollout.waitingCount',
    defaultMessage: '{count, plural, =0 {} one {# post waits for its time.} other {# posts wait for their time.}}',
  },
  keyPosted: { id: 'app.sml.releases.rollout.key.posted', defaultMessage: 'posted' },
  keyScheduled: { id: 'app.sml.releases.rollout.key.scheduled', defaultMessage: 'scheduled' },
  keyDraft: { id: 'app.sml.releases.rollout.key.draft', defaultMessage: '{count, plural, one {draft} other {drafts}}' },
  keyFailed: { id: 'app.sml.releases.rollout.key.failed', defaultMessage: 'failed' },
  keyNeedAsset: { id: 'app.sml.releases.rollout.key.needAsset', defaultMessage: 'need an asset' },
  keyReady: { id: 'app.sml.releases.rollout.key.ready', defaultMessage: 'ready to plan' },
  keyNothingOut: {
    id: 'app.sml.releases.rollout.key.nothingOut',
    defaultMessage: 'Nothing goes out before you schedule it.',
  },
  planAll: {
    id: 'app.sml.releases.rollout.planAll',
    defaultMessage: '{count, plural, one {Plan # post} other {Plan all # posts}}',
  },
  planAllCost: {
    id: 'app.sml.releases.rollout.planAll.cost',
    defaultMessage: '{count, plural, one {Adds # draft} other {Adds # drafts}} · uses no scheduled posts',
  },
  scheduleAllFit: {
    id: 'app.sml.releases.rollout.scheduleAll.fit',
    defaultMessage: '{fits} fit · {left, plural, one {# stays a draft} other {# stay drafts}}',
  },
  scheduleAllUses: {
    id: 'app.sml.releases.rollout.scheduleAll.uses',
    defaultMessage: '{count, plural, one {Uses # scheduled post} other {Uses # scheduled posts}}',
  },
  nextPost: { id: 'app.sml.releases.rollout.nextPost', defaultMessage: 'Next post' },
  addedResult: {
    id: 'app.sml.releases.rollout.added',
    defaultMessage: '{count, plural, =0 {No new draft.} one {# draft added.} other {# drafts added.}}',
  },
  // ---- Channels line
  channelsLine: {
    id: 'app.sml.releases.rollout.channels.line',
    defaultMessage: 'Each step can change its own channels.',
  },
  channelsZone: {
    id: 'app.sml.releases.rollout.channels.zone',
    defaultMessage: 'Times in the artist’s time zone ({zone}).',
  },
  channelsReconnect: {
    id: 'app.sml.releases.rollout.channels.reconnect',
    defaultMessage: '{names} {count, plural, one {needs} other {need}} Reconnect.',
  },
  // QA finding F02: the header says how the posts of each channel go out.
  channelsAuto: {
    id: 'app.sml.releases.rollout.channels.auto',
    defaultMessage: 'Post by themselves:',
  },
  channelsManual: {
    id: 'app.sml.releases.rollout.channels.manual',
    defaultMessage: 'Post it yourself (we remind you at the time):',
  },
  channelsNoneConnected: {
    id: 'app.sml.releases.rollout.channels.noneConnected',
    defaultMessage:
      'No channel is connected, so no post goes out by itself. Each post comes to you as a "post it yourself" reminder at its time.',
  },
  connectChannel: { id: 'app.sml.releases.rollout.connectChannel', defaultMessage: 'Connect a channel' },
  scheduleSplit: {
    id: 'app.sml.releases.rollout.scheduleAll.split',
    defaultMessage:
      '{auto, plural, one {# goes out by itself} other {# go out by themselves}} · {manual, plural, one {# reminder to post it yourself} other {# reminders to post it yourself}}',
  },
  channelsNone: {
    id: 'app.sml.releases.rollout.channels.none',
    defaultMessage: 'Connect a channel to plan posts',
  },
  channelsQuota: {
    id: 'app.sml.releases.rollout.channels.quota',
    defaultMessage: '{month}: {used} of {limit} scheduled posts used',
  },
  changeChannels: { id: 'app.sml.releases.rollout.channels.change', defaultMessage: 'Change channels' },
  changeChannelsTitle: {
    id: 'app.sml.releases.rollout.channels.dialog.title',
    defaultMessage: 'Channels of new steps',
  },
  changeChannelsBody: {
    id: 'app.sml.releases.rollout.channels.dialog.body',
    defaultMessage: 'A step with its own channels keeps them. A story goes only to the channels that take stories.',
  },
  changeChannelsSave: { id: 'app.sml.releases.rollout.channels.dialog.save', defaultMessage: 'Save' },
  // ---- Phases and rail
  phaseBuild: { id: 'app.sml.releases.rollout.phase.build', defaultMessage: 'Build-up' },
  phaseBuildSub: {
    id: 'app.sml.releases.rollout.phase.build.sub',
    defaultMessage: 'Announce, tease and count down to release day',
  },
  phaseRelease: { id: 'app.sml.releases.rollout.phase.release', defaultMessage: 'Release day' },
  phaseAfter: { id: 'app.sml.releases.rollout.phase.after', defaultMessage: 'After release' },
  phaseAfterSub: { id: 'app.sml.releases.rollout.phase.after.sub', defaultMessage: 'Keep it in the feed' },
  phaseRailAfter: { id: 'app.sml.releases.rollout.phase.rail.after', defaultMessage: 'After' },
  phaseDone: {
    id: 'app.sml.releases.rollout.phase.done',
    defaultMessage: '{done} of {count, plural, one {# step} other {# steps}} done',
  },
  today: { id: 'app.sml.releases.rollout.today', defaultMessage: 'Today' },
  railLabel: { id: 'app.sml.releases.rollout.rail.label', defaultMessage: 'The rollout from {first} to {last}' },
  // ---- Step card
  formatStory: { id: 'app.sml.releases.rollout.format.story', defaultMessage: 'Story' },
  formatPost: { id: 'app.sml.releases.rollout.format.post', defaultMessage: 'Feed post' },
  formatReel: { id: 'app.sml.releases.rollout.format.reel', defaultMessage: 'Reel' },
  /** A step whose planned day had passed: the plan moved it into the days left (owner ruling 2026-10-20). */
  stepMoved: { id: 'app.sml.releases.rollout.step.moved', defaultMessage: 'Moved: the original day had passed' },
  usesAsset: { id: 'app.sml.releases.rollout.step.uses', defaultMessage: 'Uses <b>{label}</b> from the kit.' },
  usesFile: { id: 'app.sml.releases.rollout.step.usesFile', defaultMessage: 'Uses a file that you picked.' },
  needsReel: {
    id: 'app.sml.releases.rollout.step.needsReel',
    defaultMessage:
      'Needs an <b>AI reel</b> of {title}. We make it with the default settings, and the step fills when it is ready.',
  },
  needsSecondReel: {
    id: 'app.sml.releases.rollout.step.needsSecondReel',
    defaultMessage: 'Needs a <b>second AI reel</b>, from another part of the song. It does not use the teaser again.',
  },
  needsCountdowns: {
    id: 'app.sml.releases.rollout.step.needsCountdowns',
    defaultMessage:
      'Needs the <b>countdown stories</b>. One run makes 7, 3 and 1 days to go. Their drafts are added when the images are ready.',
  },
  sameCountdownRun: {
    id: 'app.sml.releases.rollout.step.sameRun',
    defaultMessage: 'Made by the same run as the 7-day countdown.',
  },
  needsKitAsset: {
    id: 'app.sml.releases.rollout.step.needsKitAsset',
    defaultMessage: 'Needs the <b>{asset}</b> in the kit. It is free, and its drafts are added when it is ready.',
  },
  making: { id: 'app.sml.releases.rollout.step.making', defaultMessage: 'Making · {percent}%' },
  makingFills: {
    id: 'app.sml.releases.rollout.step.makingFills',
    defaultMessage: 'The step fills when it is ready.',
  },
  draftLine: {
    id: 'app.sml.releases.rollout.step.draftLine',
    defaultMessage:
      'The {names} {count, plural, one {post is a draft} other {posts are drafts}}. It does not go out until you schedule it.',
  },
  failedLine: {
    id: 'app.sml.releases.rollout.step.failedLine',
    defaultMessage: '{name} refused the post. {reason}',
  },
  doesNotFit: {
    id: 'app.sml.releases.rollout.step.doesNotFit',
    defaultMessage: '<b>Does not fit {month}.</b> {month} has no scheduled posts left for it. It stays a draft.',
  },
  staysDraft: { id: 'app.sml.releases.rollout.step.staysDraft', defaultMessage: 'Stays a draft' },
  assetReady: { id: 'app.sml.releases.rollout.step.assetReady', defaultMessage: 'Asset ready' },
  needsTheCountdowns: {
    id: 'app.sml.releases.rollout.step.needsTheCountdowns',
    defaultMessage: 'Needs the countdowns',
  },
  planStep: { id: 'app.sml.releases.rollout.step.plan', defaultMessage: 'Plan this step' },
  // ---- No channel and no handle (QA 2026-10-28, imp-1, imp-2): a draft needs a place to post.
  addWherePost: { id: 'app.sml.releases.rollout.noChannels.add', defaultMessage: 'Add where you post' },
  noChannelsStep: {
    id: 'app.sml.releases.rollout.noChannels.step',
    defaultMessage: 'Add a channel or a handle first, so that we know where this post goes.',
  },
  noChannelsError: {
    id: 'app.sml.releases.rollout.noChannels.error',
    defaultMessage:
      'Add a channel or a handle first. Open Channels, connect an account or type your handle, then plan this step again.',
  },
  noChannelsBody: {
    id: 'app.sml.releases.rollout.noChannels.body',
    defaultMessage:
      '{ready, plural, =0 {Tell us where you post to plan the posts.} one {# post is ready. Tell us where you post to plan it.} other {# posts are ready. Tell us where you post to plan them.}}',
  },
  countPlannedSteps: {
    id: 'app.sml.releases.rollout.count.plannedSteps',
    defaultMessage: 'of {total, plural, one {# step} other {# steps}} planned',
  },
  makeReel: { id: 'app.sml.releases.rollout.step.makeReel', defaultMessage: 'Make an AI reel' },
  makeCountdowns: { id: 'app.sml.releases.rollout.step.makeCountdowns', defaultMessage: 'Make the countdowns' },
  makeIt: { id: 'app.sml.releases.rollout.step.makeIt', defaultMessage: 'Make it' },
  reelCost: {
    id: 'app.sml.releases.rollout.step.reelCost',
    defaultMessage: '1 video credit{left, select, none {} other { · you have {left}}}',
  },
  freeImages: { id: 'app.sml.releases.rollout.step.freeImages', defaultMessage: 'Free · images' },
  schedulePlatform: { id: 'app.sml.releases.rollout.step.schedule', defaultMessage: 'Schedule {name}' },
  scheduleDrafts: {
    id: 'app.sml.releases.rollout.step.scheduleDrafts',
    defaultMessage: '{count, plural, one {Schedule # draft} other {Schedule # drafts}}',
  },
  scheduled: { id: 'app.sml.releases.rollout.step.scheduled', defaultMessage: 'Scheduled' },
  open: { id: 'app.sml.releases.rollout.step.open', defaultMessage: 'Open' },
  reconnect: { id: 'app.sml.releases.rollout.step.reconnect', defaultMessage: 'Reconnect' },
  // ---- Channel pill states
  pillNotPlanned: { id: 'app.sml.releases.rollout.pill.notPlanned', defaultMessage: 'Not planned' },
  pillDraft: { id: 'app.sml.releases.rollout.pill.draft', defaultMessage: 'Draft' },
  pillScheduled: { id: 'app.sml.releases.rollout.pill.scheduled', defaultMessage: 'Scheduled' },
  pillPosted: { id: 'app.sml.releases.rollout.pill.posted', defaultMessage: 'Posted' },
  pillFailed: { id: 'app.sml.releases.rollout.pill.failed', defaultMessage: 'Failed' },
  pillNeedsYou: { id: 'app.sml.releases.rollout.pill.needsYou', defaultMessage: 'Needs you' },
  pillPublishing: { id: 'app.sml.releases.rollout.pill.publishing', defaultMessage: 'Going out' },
  // ---- Skipped steps (c2)
  skippedLine: { id: 'app.sml.releases.rollout.skipped.line', defaultMessage: 'Skipped step: {name}' },
  undoSkip: { id: 'app.sml.releases.rollout.skipped.undo', defaultMessage: 'Undo skip' },
  // ---- Milestones (a1, a2, b1)
  milestone: { id: 'app.sml.releases.rollout.milestone', defaultMessage: 'Milestone' },
  fromKit: { id: 'app.sml.releases.rollout.milestone.fromKit', defaultMessage: 'From the kit' },
  youDoIt: { id: 'app.sml.releases.rollout.milestone.youDoIt', defaultMessage: 'You do it' },
  isOut: { id: 'app.sml.releases.rollout.milestone.out', defaultMessage: '{title} is out' },
  isOutSub: {
    id: 'app.sml.releases.rollout.milestone.out.sub',
    defaultMessage: 'The stores release it. The pre-save page becomes the SmartLink.',
  },
  lyricPublic: {
    id: 'app.sml.releases.rollout.lyric.public',
    defaultMessage: 'Lyric video goes public on YouTube',
  },
  lyricTitle: { id: 'app.sml.releases.rollout.lyric.title', defaultMessage: 'Lyric video on YouTube' },
  lyricNone: {
    id: 'app.sml.releases.rollout.lyric.none',
    defaultMessage: 'The kit has no lyric video yet. It goes public at 00:00 on release day.',
  },
  lyricMake: { id: 'app.sml.releases.rollout.lyric.make', defaultMessage: 'Make the lyric video' },
  lyricCost: { id: 'app.sml.releases.rollout.lyric.cost', defaultMessage: '3 credits' },
  lyricNeedsMaster: { id: 'app.sml.releases.rollout.lyric.needsMaster', defaultMessage: 'Add the song file' },
  lyricNeedsLyrics: { id: 'app.sml.releases.rollout.lyric.needsLyrics', defaultMessage: 'Add the lyrics' },
  lyricMaking: {
    id: 'app.sml.releases.rollout.lyric.making',
    defaultMessage: 'It is published on release day when it is ready.',
  },
  lyricNotSet: { id: 'app.sml.releases.rollout.lyric.notSet', defaultMessage: 'Not set yet.' },
  lyricPublishOnDay: {
    id: 'app.sml.releases.rollout.lyric.publishOnDay',
    defaultMessage: 'Publish on release day',
  },
  lyricSet: {
    id: 'app.sml.releases.rollout.lyric.set',
    defaultMessage: 'Private now · public at {time}',
  },
  lyricLive: { id: 'app.sml.releases.rollout.lyric.live', defaultMessage: 'Public on YouTube' },
  lyricChangeTime: { id: 'app.sml.releases.rollout.lyric.changeTime', defaultMessage: 'Change time' },
  lyricLocked: {
    id: 'app.sml.releases.rollout.lyric.locked',
    defaultMessage: 'Full-song videos are on Pro, $39 a month.',
  },
  seePro: { id: 'app.sml.releases.rollout.lyric.seePro', defaultMessage: 'See Pro' },
  connectYouTube: {
    id: 'app.sml.releases.rollout.lyric.connectYouTubeConnections',
    defaultMessage: 'Connect YouTube in Connections',
  },
  // The native apps read the old Channels copy (navigation v2 renamed the page Connections).
  connectYouTubeNative: {
    id: 'app.sml.releases.rollout.lyric.connectYouTube',
    defaultMessage: 'Connect YouTube in Channels',
  },
  canvasTitle: { id: 'app.sml.releases.rollout.canvas.title', defaultMessage: 'Add the Canvas on Spotify' },
  canvasSub: {
    id: 'app.sml.releases.rollout.canvas.sub',
    defaultMessage: 'Spotify has no API for this. Add the loop in Spotify for Artists, then mark it here. No reminder.',
  },
  canvasDone: { id: 'app.sml.releases.rollout.canvas.done', defaultMessage: 'The Canvas is on Spotify' },
  canvasNoLoop: {
    id: 'app.sml.releases.rollout.canvas.noLoop',
    defaultMessage: 'Make the Canvas loop in the Kit first.',
  },
  canvasOpenKit: { id: 'app.sml.releases.rollout.canvas.openKit', defaultMessage: 'Open Kit' },
  canvasHow: { id: 'app.sml.releases.rollout.canvas.how', defaultMessage: 'How?' },
  canvasAdded: { id: 'app.sml.releases.rollout.canvas.added', defaultMessage: 'I added it' },
  // ---- Needs you (b1)
  needsTitle: { id: 'app.sml.releases.rollout.needs.title', defaultMessage: 'Needs you' },
  needsSub: {
    id: 'app.sml.releases.rollout.needs.sub',
    defaultMessage: '{count, plural, one {# thing} other {# things}}, oldest first',
  },
  needsNone: { id: 'app.sml.releases.rollout.needs.none', defaultMessage: 'Nothing needs you now.' },
  needFailed: { id: 'app.sml.releases.rollout.needs.failed', defaultMessage: '{step} failed on {name}' },
  needDraft: { id: 'app.sml.releases.rollout.needs.draft', defaultMessage: '{step} is a draft on {name}' },
  needManual: { id: 'app.sml.releases.rollout.needs.manual', defaultMessage: '{step} waits for you on {name}' },
  needMissing: { id: 'app.sml.releases.rollout.needs.missing', defaultMessage: '{step} has no file' },
  needMissingBy: { id: 'app.sml.releases.rollout.needs.missingBy', defaultMessage: 'Make it by {day}' },
  needCanvasSub: {
    id: 'app.sml.releases.rollout.needs.canvasSub',
    defaultMessage: 'Any time. It shows from release day.',
  },
  needSchedule: { id: 'app.sml.releases.rollout.needs.schedule', defaultMessage: 'Schedule' },
  needMake: { id: 'app.sml.releases.rollout.needs.make', defaultMessage: 'Make' },
  needPostIt: { id: 'app.sml.releases.rollout.needs.postIt', defaultMessage: 'Post it' },
  needsPhone: {
    id: 'app.sml.releases.rollout.needs.phone',
    defaultMessage: '{count, plural, one {# thing needs you} other {# things need you}}',
  },
  needsShow: { id: 'app.sml.releases.rollout.needs.show', defaultMessage: 'Show' },
  needsHide: { id: 'app.sml.releases.rollout.needs.hide', defaultMessage: 'Hide' },
  pushTitle: { id: 'app.sml.releases.rollout.push.title', defaultMessage: 'Push after release' },
  pushSub: { id: 'app.sml.releases.rollout.push.sub', defaultMessage: 'When this plan ends' },
  pushBody: {
    id: 'app.sml.releases.rollout.push.body',
    defaultMessage:
      'After {day} you can add “Push this song”: one post a week for 6 weeks. It shows here when the rollout ends.',
  },
  // ---- Free plan (e1)
  monthUse: { id: 'app.sml.releases.rollout.free.month', defaultMessage: '{month} · {used} of {limit} used' },
  monthNeeds: {
    id: 'app.sml.releases.rollout.free.needs',
    defaultMessage:
      'This plan needs {needs} in {month}. {fits, plural, =0 {None fit.} other {{all, select, yes {All fit.} other {# fit.}}}}',
  },
  upsellTitle: {
    id: 'app.sml.releases.rollout.free.upsell.title',
    defaultMessage: 'Post the whole rollout on 3 channels',
  },
  upsellBody: {
    id: 'app.sml.releases.rollout.free.upsell.body',
    defaultMessage: 'Plus: 60 scheduled posts a month and 3 channels, with no mark on your assets. $19 a month.',
  },
  seePlans: { id: 'app.sml.releases.rollout.free.seePlans', defaultMessage: 'See plans' },
});
